import { DEFAULT_LOCALE, localeFromPath } from '~/constants/appLocale'

/**
 * The browser side of the marketing pages' languages (middleware/locale.global.ts).
 *
 * - On the very first visit, a browser that prefers Czech or German and lands
 *   on an English page is taken to its translation. Only in that direction: a
 *   crawler's browser is English, so Googlebot rendering /cs/pricing is never
 *   sent away from it.
 * - A marketing page read in a language makes it the remembered one, so the
 *   app opened from it continues in that language.
 *
 * Here and not on the server: the homepage is cached (ISR, nuxt.config), and a
 * redirect or cookie rendered for one visitor would be served to everyone.
 */
export default defineNuxtPlugin(() => {
  const router = useRouter()
  const appLocale = useAppLocale()
  const localize = useLocalizedPath()
  const here = location.pathname + location.search + location.hash

  const isBot = navigator.webdriver || /bot|crawl|spider|slurp|preview/i.test(navigator.userAgent)
  if (!appLocale.remembered() && !isBot && localeFromPath(location.pathname) === DEFAULT_LOCALE) {
    const detected = appLocale.detect()
    const target = detected === DEFAULT_LOCALE ? here : localize(here, detected)
    if (target !== here) {
      appLocale.remember(detected)
      // A full load rather than router.replace: the translated page then comes
      // server-rendered, and the English one is not left half hydrated (its
      // lazy sections report a mismatch when the route changes under them).
      location.replace(target)
      return
    }
  }

  // The landing page too: the initial navigation may finish before afterEach is registered.
  const landing = router.resolve(here)
  if (isLocalizedRoute(landing)) appLocale.remember(localeFromPath(landing.path))
  router.afterEach((to) => {
    if (isLocalizedRoute(to)) appLocale.remember(localeFromPath(to.path))
  })
})
